import { useEffect, useRef, useState } from 'react';
import { getEffectiveSoundOutputVolume } from './soundEffectsManager';
import {
  setExploreUnknownMission46CelloBattleActive,
  isExploreUnknownMission46CelloAllowedSound,
} from '../utils/exploreUnknownKendrexColonyAudio';

const TUNE_UP_PATH = '/sounds/tune_up.mp3';
const CELLO_MAIN_PATH = '/sounds/cello_main.mp3';

/**
 * @param {() => void} onComplete
 * @returns {HTMLAudioElement|null}
 */
function playMission46TuneUp(onComplete) {
  if (!isExploreUnknownMission46CelloAllowedSound(TUNE_UP_PATH)) {
    throw new Error(`useExploreUnknownMission46CelloAudio: disallowed path (${TUNE_UP_PATH})`);
  }
  const volume = getEffectiveSoundOutputVolume(1, TUNE_UP_PATH);
  if (volume == null) {
    onComplete();
    return null;
  }
  const audio = new Audio(TUNE_UP_PATH);
  audio.volume = volume;
  const finish = () => onComplete();
  audio.addEventListener('ended', finish, { once: true });
  audio.addEventListener('error', finish, { once: true });
  audio.play().catch((err) => {
    console.error('[ExploreUnknownMission46Cello] tune_up failed:', err?.message || err);
    finish();
  });
  return audio;
}

function playMission46PathSequence(paths) {
  let index = 0;
  const playNext = () => {
    if (index >= paths.length) {
      return;
    }
    const path = paths[index];
    index += 1;
    if (!isExploreUnknownMission46CelloAllowedSound(path)) {
      throw new Error(`useExploreUnknownMission46CelloAudio: disallowed deploy path (${path})`);
    }
    const volume = getEffectiveSoundOutputVolume(1, path);
    if (volume == null) {
      playNext();
      return;
    }
    const audio = new Audio(path);
    audio.volume = volume;
    const advance = () => playNext();
    audio.addEventListener('ended', advance, { once: true });
    audio.addEventListener('error', advance, { once: true });
    audio.play().catch((err) => {
      console.error('[ExploreUnknownMission46Cello] deploy stinger failed:', path, err?.message || err);
      advance();
    });
  };
  playNext();
}

function stopAndClearAudio(audio) {
  if (!audio) {
    return;
  }
  audio.pause();
  audio.currentTime = 0;
}

/**
 * Mission 46 (Kendrex Cello boss): tune_up once, cello_main loop, deploy stingers from gameState.
 * @param {object|null|undefined} gameState
 * @param {string|null|undefined} gameId
 */
export function useExploreUnknownMission46CelloAudio(gameState, gameId) {
  const mainLoopRef = useRef(null);
  const tuneUpAudioRef = useRef(null);
  /** @type {import('react').MutableRefObject<{ gameId: string, status: 'playing' | 'done' } | null>} */
  const tuneUpSessionRef = useRef(null);
  const lastProcessedSfxSeqRef = useRef(0);
  const [mainLoopAllowed, setMainLoopAllowed] = useState(false);

  const missionIndex = Number(gameState?.exploreUnknownMissionIndex);
  const isMission46 = missionIndex === 46;

  useEffect(() => {
    setExploreUnknownMission46CelloBattleActive(isMission46);
    return () => {
      setExploreUnknownMission46CelloBattleActive(false);
      stopAndClearAudio(mainLoopRef.current);
      mainLoopRef.current = null;
      stopAndClearAudio(tuneUpAudioRef.current);
      tuneUpAudioRef.current = null;
    };
  }, [isMission46]);

  useEffect(() => {
    if (!isMission46 || !gameId) {
      setMainLoopAllowed(false);
      tuneUpSessionRef.current = null;
      stopAndClearAudio(tuneUpAudioRef.current);
      tuneUpAudioRef.current = null;
      return undefined;
    }

    if (gameState?.exploreUnknownMission46CelloTuneUpPending !== true) {
      setMainLoopAllowed(true);
      return undefined;
    }

    const session = tuneUpSessionRef.current;
    if (session?.gameId === gameId && session.status === 'done') {
      setMainLoopAllowed(true);
      return undefined;
    }
    if (session?.gameId === gameId && session.status === 'playing') {
      return undefined;
    }

    tuneUpSessionRef.current = { gameId, status: 'playing' };
    setMainLoopAllowed(false);
    stopAndClearAudio(tuneUpAudioRef.current);
    tuneUpAudioRef.current = playMission46TuneUp(() => {
      if (tuneUpSessionRef.current?.gameId === gameId) {
        tuneUpSessionRef.current = { gameId, status: 'done' };
      }
      setMainLoopAllowed(true);
    });

    return () => {
      stopAndClearAudio(tuneUpAudioRef.current);
      tuneUpAudioRef.current = null;
    };
  }, [isMission46, gameId, gameState?.exploreUnknownMission46CelloTuneUpPending]);

  useEffect(() => {
    if (!isMission46 || !mainLoopAllowed) {
      if (mainLoopRef.current) {
        stopAndClearAudio(mainLoopRef.current);
        mainLoopRef.current = null;
      }
      return undefined;
    }
    const volume = getEffectiveSoundOutputVolume(1, CELLO_MAIN_PATH);
    if (volume == null) {
      if (mainLoopRef.current) {
        stopAndClearAudio(mainLoopRef.current);
        mainLoopRef.current = null;
      }
      return undefined;
    }
    if (!mainLoopRef.current) {
      const audio = new Audio(CELLO_MAIN_PATH);
      audio.loop = true;
      audio.volume = volume;
      mainLoopRef.current = audio;
      audio.play().catch((err) => {
        console.error('[ExploreUnknownMission46Cello] main loop failed:', err?.message || err);
      });
    } else {
      mainLoopRef.current.volume = volume;
      if (mainLoopRef.current.paused) {
        mainLoopRef.current.play().catch((err) => {
          console.error('[ExploreUnknownMission46Cello] main loop resume failed:', err?.message || err);
        });
      }
    }
    return undefined;
  }, [isMission46, mainLoopAllowed]);

  useEffect(() => {
    if (!isMission46) {
      lastProcessedSfxSeqRef.current = 0;
      return;
    }
    const seq = Number(gameState?.exploreUnknownMission46CelloSfxSeq || 0);
    if (!Number.isInteger(seq) || seq < 0) {
      throw new Error(
        `useExploreUnknownMission46CelloAudio: invalid sfx seq (${String(gameState?.exploreUnknownMission46CelloSfxSeq)})`
      );
    }
    if (seq === 0 || seq === lastProcessedSfxSeqRef.current) {
      return;
    }
    const paths = gameState?.exploreUnknownMission46CelloSfxPaths;
    if (!Array.isArray(paths) || paths.length === 0) {
      throw new Error(
        `useExploreUnknownMission46CelloAudio: sfx seq ${seq} without exploreUnknownMission46CelloSfxPaths`
      );
    }
    lastProcessedSfxSeqRef.current = seq;
    playMission46PathSequence(paths);
  }, [isMission46, gameState?.exploreUnknownMission46CelloSfxSeq, gameState?.exploreUnknownMission46CelloSfxPaths]);
}
